import { FormationDetail } from '../../types/services';
import { themeColors } from '../../utils/colors';
import { isEnrollmentEnabled } from '../../utils/flags';

function enc(v: string): string {
  try { return encodeURIComponent(v); } catch { return v; }
}

/**
 * Obtient le prix d'une formation basé sur son ID
 * @param id ID de la formation
 * @returns Prix de la formation
 */
function getFormationPrice(id: string): number {
  // Mapping des formations avec leurs prix
  const formationPrices = {
    'ia-fondamentaux': 50,
    'diverses-ia': 50,
    'ia-chatgpt': 80,
    'ia-claude': 80,
    'crypto-actifs': 450,
    'RGPD': 250,
    'facture-electronique': 200
  };

  // Retourne le prix correspondant ou un prix par défaut si non trouvé
  return formationPrices[id] || 300;
}

/**
 * Obtient le texte formaté du prix d'une formation avec mention "par personne" si nécessaire
 * @param id ID de la formation
 * @returns Texte du prix formaté
 */
function getFormationPriceText(id: string): string {
  const price = getFormationPrice(id);
  const formationsParPersonne = ['ia-fondamentaux', 'diverses-ia', 'ia-chatgpt','ia-claude' ];

  if (formationsParPersonne.includes(id)) {
    return `${price}€ par personne`;
  }

  return `${price}€`;
}

export class FormationsTemplates {
  /**
   * Génère le HTML pour le fond de la section Formations
   */
  public static generateBackgroundHTML(): string {
    return `
      <div class="absolute inset-0 overflow-hidden">
        <!-- Grille de fond modernisée avec motif subtil -->
        <div class="absolute inset-0 opacity-20" style="background-image: radial-gradient(rgba(237, 76, 103, 0.1) 1px, transparent 1px), radial-gradient(rgba(52, 152, 219, 0.1) 1px, transparent 1px); background-size: 30px 30px, 25px 25px; background-position: 0 0, 15px 15px;"></div>

        <!-- Dégradé de fond plus intense et sombre -->
        <div class="absolute inset-0 bg-gradient-to-b from-[#0b1220] via-[#0b1220]/95 to-[#0b1220]/90"></div>

        <!-- Formes organiques vivantes - plus grandes et plus colorées -->
        <div class="absolute -top-[15%] -right-[5%] w-[70%] h-[70%] bg-gradient-to-br from-[#ed4c67]/25 to-[#3498db]/10 rounded-full blur-3xl opacity-60 wave-anim" style="animation-delay: 0.5s;"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[80%] h-[80%] bg-gradient-to-tr from-[#3498db]/25 to-[#ed4c67]/10 rounded-full blur-3xl opacity-60 wave-anim-alt"></div>

        <!-- Cercles radiatifs pulsants -->
        <div class="radiate absolute top-1/4 left-1/4 w-40 h-40 bg-[#ed4c67]/10 rounded-full blur-xl"></div>
        <div class="radiate absolute bottom-1/3 right-1/4 w-60 h-60 bg-[#3498db]/10 rounded-full blur-xl" style="animation-delay: 1s;"></div>
        <div class="radiate absolute top-2/3 left-1/3 w-32 h-32 bg-[#e67e22]/10 rounded-full blur-xl" style="animation-delay: 2s;"></div>

        <!-- Vignette centrale plus profonde pour mettre en valeur les cards -->
        <div class="absolute inset-0 pointer-events-none" style="background: radial-gradient(ellipse at center, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.10) 40%, rgba(0,0,0,0.42) 100%);"></div>

        <!-- Assombrissement haut/bas pour plus de profondeur -->
        <div class="absolute inset-x-0 top-0 h-64 pointer-events-none" style="background: linear-gradient(to bottom, rgba(0,0,0,0.38), rgba(0,0,0,0.0));"></div>
        <div class="absolute inset-x-0 bottom-0 h-64 pointer-events-none" style="background: linear-gradient(to top, rgba(0,0,0,0.38), rgba(0,0,0,0.0));"></div>

        <!-- Structure de lignes subtiles qui traversent l'écran -->
        <div class="absolute inset-0 opacity-10">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,50 C30,30 70,70 100,50 L100,100 L0,100 Z" fill="#3498db" fill-opacity="0.1"></path>
            <path d="M0,60 C40,40 60,80 100,60 L100,100 L0,100 Z" fill="#ed4c67" fill-opacity="0.1"></path>
            <path d="M0,70 C50,50 50,90 100,70 L100,100 L0,100 Z" fill="#e67e22" fill-opacity="0.05"></path>
          </svg>
        </div>
      </div>
    `;
  }

  /**
   * Génère le HTML pour le titre de la section Formations
   */
  public static generateTitleSectionHTML(): string {
    return `
      <!-- Titre avec animation -->
      <h2 class="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight fade-in-up delay-200 text-center" style="border-left: none !important; padding-left: 0 !important;">
        <span class="text-white" style="text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);"><span class="text-gradient">Nos Formations</span></span>
      </h2>

      <!-- Description -->
      <p class="text-xl text-white/80 max-w-3xl leading-relaxed fade-in-up delay-300">
        Des programmes adaptés à chaque besoin pour comprendre et maîtriser les technologies de demain.
      </p>

      <!-- Élément décoratif -->
      <div class="w-24 h-1 bg-gradient-to-r from-[${themeColors['won-pink']}] to-[${themeColors['won-blue']}] mt-8 rounded-full fade-in-up delay-400"></div>
    `;
  }

  /**
   * Génère le HTML pour les onglets des formations
   */
  public static generateTabsHTML(formations: FormationDetail[]): string {
    return `
      <div class="formation-tabs-shell relative rounded-full bg-white/5 backdrop-blur-md border border-white/10 px-2 py-2">
        <div class="formation-tabs-scroll flex flex-wrap justify-center gap-3 relative">
          ${formations.map((formation, index) => {
            // Détection image
            const isImage = typeof formation.icon === 'string'
              && (
                formation.icon.endsWith('.png')
                || formation.icon.endsWith('.jpg')
                || formation.icon.endsWith('.jpeg')
                || formation.icon.endsWith('.webp')
              );
            return `
              <button data-formation-tab="${formation.id}" class="formation-tab-btn px-4 py-2 text-white/90 font-medium rounded-3xl transition-all duration-300 hover:text-white flex items-center hover:bg-[${formation.featureColor}]/30 hover:shadow-md hover:shadow-[${formation.featureColor}]/20" data-index="${index}">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-[${formation.featureColor}]/40 flex items-center justify-center mr-2 border border-[${formation.featureColor}]/50">
                  ${
                    isImage
                      ? `<img src="${formation.icon}" alt="" aria-hidden="true" class="w-6 h-6 object-contain rounded-full p-0.5" />`
                      : `<svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="${formation.icon}" />
                        </svg>`
                  }
                </div>
                <span>${formation.title}</span>
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }


  /**
   * Génère le HTML pour le contenu d'une formation
   */
  public static generateFormationContentHTML(formation: FormationDetail): string {
    const { id, title, duration, level, audience, description, modules, benefits, featureColor, icon } = formation;

    return `
      <div data-formation-content="${id}" class="formation-content opacity-0 transform translate-y-4 transition-all duration-500 hidden">
        <div class="bg-gradient-to-br from-[#34495e]/90 to-[#34495e]/80 backdrop-blur-xl border border-[${featureColor}]/30 rounded-2xl p-8 md:p-10 relative overflow-hidden shine-effect shadow-lg shadow-[${featureColor}]/10 shadow-pulse">
          <!-- Éléments décoratifs lumineux - intensifiés et harmonisés -->
          <div class="absolute -top-20 -right-20 w-60 h-60 bg-[${featureColor}] rounded-full filter blur-[80px] opacity-30 z-0 radiate"></div>
          <div class="absolute -bottom-20 -left-20 w-60 h-60 bg-[${themeColors['won-blue']}] rounded-full filter blur-[80px] opacity-20 z-0 radiate" style="animation-delay: 2s;"></div>

          <!-- Barre de couleur en haut -->
          <div class="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[${featureColor}] to-[${featureColor}]/30"></div>

          <!-- En-tête avec dégradé amélioré -->
          <div class="flex flex-col md:flex-row items-start md:items-center mb-8 pb-8  relative z-10">
            <div class="flex-shrink-0 mb-5 md:mb-0 md:mr-8">
              <div class="relative">
                <!-- Halo lumineux derrière l'icône - intensifié -->
                <div class="absolute inset-0 bg-[${featureColor}]/60 rounded-xl filter blur-xl"></div>

                <div class="w-20 h-20 rounded-xl flex items-center justify-center bg-gradient-to-br from-[${featureColor}]/80 to-[${featureColor}]/40 relative z-10 border border-[${featureColor}]/60 shadow-lg shadow-[${featureColor}]/30 morphing-shape">
                ${
                  icon && icon.endsWith && (icon.endsWith('.png') || icon.endsWith('.jpg') || icon.endsWith('.jpeg') || icon.endsWith('.webp'))
                    ? `<img src="${icon}" alt="${title}" class="w-18 h-18 object-contain rounded-3xl  p-2" />`
                    : `<svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="${icon}" />
                      </svg>`
                }
              </div>

              </div>
            </div>
            <div>
              <h3 class="text-3xl md:text-4xl font-bold mb-3 text-white" style="text-shadow: 0 0 15px ${featureColor};">${title}</h3>
              <p class="text-white/90 text-xl leading-relaxed">${description}</p>
            </div>
          </div>

          <!-- Informations pratiques améliorées -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8 relative z-10">
            <div class="bg-gradient-to-br from-white/15 to-[${featureColor}]/10 backdrop-blur-sm rounded-xl p-4 border border-[${featureColor}]/30 flex items-center group hover:bg-[${featureColor}]/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[${featureColor}]/20">
              <div class="w-12 h-12 rounded-full flex items-center justify-center bg-[${featureColor}]/40 text-white border border-[${featureColor}]/50 mr-4 shadow-inner shadow-[${featureColor}]/20">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <span class="text-white/70 text-sm">Durée</span>
                <p class="text-white font-medium text-lg">${duration}</p>
              </div>
            </div>
            <div class="bg-gradient-to-br from-white/15 to-[${featureColor}]/10 backdrop-blur-sm rounded-xl p-4 border border-[${featureColor}]/30 flex items-center group hover:bg-[${featureColor}]/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[${featureColor}]/20">
              <div class="w-12 h-12 aspect-square rounded-full flex-none flex items-center justify-center bg-[${featureColor}]/40 text-white border border-[${featureColor}]/50 mr-4 shadow-inner shadow-[${featureColor}]/20" style="transform: translateZ(0);">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <span class="text-white/70 text-sm">Niveau</span>
                <p class="text-white font-medium text-lg">${level}</p>
              </div>
            </div>
            <div class="bg-gradient-to-br from-white/15 to-[${featureColor}]/10 backdrop-blur-sm rounded-xl p-4 border border-[${featureColor}]/30 flex items-center group hover:bg-[${featureColor}]/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[${featureColor}]/20">
              <div class="w-12 h-12 aspect-square rounded-full flex-none flex items-center justify-center bg-[${featureColor}]/40 text-white border border-[${featureColor}]/50 mr-4 shadow-inner shadow-[${featureColor}]/20" style="transform: translateZ(0);">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <span class="text-white/70 text-sm">Public</span>
                <p class="text-white font-medium text-lg">${audience}</p>
              </div>
            </div>
          </div>

          <!-- Programme et avantages améliorés -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            <!-- Programme de formation -->
            <div class="md:col-span-2 relative z-20">
              <h4 class="text-2xl font-bold mb-6 flex items-center text-white">
                <span class="w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-[${featureColor}]/40 text-white border border-[${featureColor}]/50 shadow-md shadow-[${featureColor}]/15">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" shape-rendering="geometricPrecision">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" vector-effect="non-scaling-stroke" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </span>
                Programme
              </h4>

              <div class="space-y-4">
                ${modules.map((module, index) => `
                  <div class="flex items-start p-4 rounded-xl bg-gradient-to-r from-[${featureColor}]/20 to-white/10 backdrop-blur-sm border border-[${featureColor}]/20 hover:bg-[${featureColor}]/25 hover:border-[${featureColor}]/40 transition-all duration-300 transform hover:translate-x-1 hover:shadow-lg hover:shadow-[${featureColor}]/10 group">
                    <div class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-[${featureColor}]/40 text-white border border-[${featureColor}]/50 mr-4 mt-0.5 group-hover:scale-110 transition-transform duration-300 shadow-md shadow-[${featureColor}]/10">
                      <span class="text-xs font-bold">${index + 1}</span>
                    </div>
                    <span class="text-white/95 text-lg">${module}</span>
                  </div>
                `).join('')}
              </div>
            </div>

            <!-- Avantages -->
            <div class="relative z-20">
              <h4 class="text-2xl font-bold mb-6 flex items-center text-white">
                <span class="w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-gradient-to-br from-[${featureColor}]/40 to-[${themeColors['won-blue']}]/30 text-white border border-[${featureColor}]/40 shadow-md shadow-[${featureColor}]/15">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" shape-rendering="geometricPrecision">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" vector-effect="non-scaling-stroke" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                  </span>
                  Ce que vous en tirerez
                </h4>

                <div class="space-y-4">
                  ${benefits.map(benefit => `
                    <div class="p-4 rounded-xl bg-gradient-to-br from-[${featureColor}]/30 to-[${themeColors['won-blue']}]/20 border border-[${featureColor}]/30 hover:from-[${featureColor}]/40 hover:to-[${themeColors['won-blue']}]/30 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-xl hover:shadow-[${featureColor}]/15 group">
                      <div class="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white mr-2 flex-shrink-0 mt-0.5 group-hover:text-[${featureColor}] transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" shape-rendering="geometricPrecision">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" vector-effect="non-scaling-stroke" d="M5 13l4 4L19 7" />
                        </svg>
                        <span class="text-white/95 text-lg">${benefit}</span>
                      </div>
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>

            <!-- Boutons d'action améliorés -->
            <div class="mt-12 text-center relative z-10 flex flex-col sm:flex-row justify-center gap-4">
              ${isEnrollmentEnabled() ? `
              <!-- Inscription en ligne activée -->
              <div class="flex flex-col items-center gap-2">
                <button
                  data-formation="${id}"
                  data-formation-name="${title}"
                  data-formation-price="${getFormationPriceText(id)}"
                  class="btn-formation-purchase group relative overflow-hidden rounded-full py-4 px-6 inline-flex items-center justify-center shine-effect"
                  title="S’inscrire à cette formation"
                  aria-label="S’inscrire à ${title}"
                >
                  <span class="absolute inset-0 bg-gradient-to-r from-[${featureColor}] to-[${featureColor}]/80 rounded-xl transition-all duration-300 dynamic-glow"></span>
                  <span class="absolute inset-0 bg-gradient-to-r from-[${featureColor}] via-[${featureColor}]/90 to-[${featureColor}]/70 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-105 group-hover:scale-100 shadow-lg shadow-[${featureColor}]/30"></span>
                  <span class="relative flex items-center justify-center z-10 font-bold text-white transition-transform duration-300 group-hover:scale-105">
                    <span class="relative z-10 flex items-center">
                      S’inscrire
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h10M8 21h8a2 2 0 002-2v-6a2 2 0 00-2-2H8a2 2 0 00-2 2v6a2 2 0 002 2zm0-12V7a4 4 0 118 0v2" />
                      </svg>
                    </span>
                  </span>
                </button>
                <span class="text-xs text-white/70 flex items-center gap-1 justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm-3 8V6a3 3 0 116 0v3H9z"/>
                  </svg>
                  Paiement sécurisé par Stripe
                </span>
              </div>
              ` : `
              <!-- Inscription en ligne désactivée -->
              <div class="flex flex-col items-center gap-2">
                <a
                  href="contact.html?subject=${enc(`Demande inscription ${title}`)}"
                  class="group relative overflow-hidden rounded-full py-4 px-6 inline-flex items-center justify-center shine-effect"
                  title="Nous contacter"
                  aria-label="Nous contacter à propos de ${title}"
                >
                  <span class="absolute inset-0 bg-gradient-to-r from-[${featureColor}] to-[${featureColor}]/80 rounded-xl transition-all duration-300 dynamic-glow"></span>
                  <span class="absolute inset-0 bg-gradient-to-r from-[${featureColor}] via-[${featureColor}]/90 to-[${featureColor}]/70 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-105 group-hover:scale-100 shadow-lg shadow-[${featureColor}]/30"></span>
                  <span class="relative z-10 font-bold text-white flex items-center transition-transform duration-300 group-hover:scale-105">
                    Pré-inscription
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </a>
                <span class="text-xs text-white/70">Inscriptions en ligne bientôt disponibles</span>
              </div>
              `}
            </div>

            <!-- Éléments flottants pour un effet de profondeur -->
            <div class="absolute top-5 left-10 w-10 h-10 bg-[${featureColor}]/30 rounded-full filter blur-md wave-anim z-0 pointer-events-none"></div>
            <div class="absolute bottom-10 right-5 w-8 h-8 bg-[${featureColor}]/25 rounded-full filter blur-md wave-anim-alt z-0 pointer-events-none" style="animation-delay: 1s;"></div>
            <div class="absolute top-1/2 right-10 w-6 h-6 bg-[${featureColor}]/30 rounded-full filter blur-md wave-anim z-0 pointer-events-none" style="animation-delay: 1.5s;"></div>
            <div class="absolute bottom-1/3 left-16 w-4 h-4 bg-[${featureColor}]/35 rounded-full filter blur-sm wave-anim-alt z-0 pointer-events-none" style="animation-delay: 2s;"></div>
          </div>
        </div>
    `;
  }

  /**
   * Génère le HTML pour le CTA de la section Formations
   */
  public static generateCtaSectionHTML(): string {
    return `
      <div class="formations-cta-wrapper relative mt-10 overflow-hidden rounded-full p-0.5 border border-[${themeColors['won-pink']}]/30 text-white group hover:shadow-2xl hover:shadow-[${themeColors['won-pink']}]/20 transition-all duration-700">
        <!-- Bordure animée intensifiée -->
        <div class="absolute inset-0 bg-gradient-to-r from-[${themeColors['won-pink']}] via-[${themeColors['won-blue']}] to-[${themeColors['won-pink']}] opacity-40 bg-[length:200%_100%] animate-gradient-flow"></div>

        <!-- Contenu avec fond semi-transparent -->
        <div class="formations-cta-inner relative bg-gradient-to-br from-[#34495e]/90 to-[#131313]/90 backdrop-blur-xl rounded-xl p-8 md:p-10">
          <!-- Éléments décoratifs lumineux intensifiés -->
          <div class="absolute -top-20 -right-20 w-80 h-80 bg-[${themeColors['won-pink']}] rounded-full filter blur-[100px] opacity-40 z-0"></div>
          <div class="absolute -bottom-20 -left-20 w-80 h-80 bg-[${themeColors['won-blue']}] rounded-full filter blur-[100px] opacity-40 z-0"></div>

          <div class="formations-cta-layout flex flex-col lg:flex-row items-center justify-between gap-8 relative z-10">
            <div class="formations-cta-text lg:w-3/3">
              <h3 class="formations-cta-title text-3xl lg:text-4xl font-bold mb-4 text-gradient">
                Formez-vous aux technologies de demain
              </h3>
              <p class="formations-cta-description text-lg text-white/90 max-w-2xl">
                Nos formations sur mesure s'adaptent à votre niveau et à vos objectifs professionnels.
              </p>
            </div>
            <div class="formations-cta-actions flex flex-wrap gap-10 fade-in-up delay-500">
                <!-- Bouton principal avec animation de gradient et effet de survol -->
                <a href="contact.html" class="formations-cta-button group relative overflow-hidden rounded-full py-4 px-8 inline-flex items-center justify-center shine-effect shadow-lg shadow-[${themeColors['won-pink']}]/20">
                  <!-- Fond animé avec effet de battement -->
                  <span class="absolute inset-0 bg-gradient-to-r from-[${themeColors['won-pink']}] to-[${themeColors['won-pink']}]/80 rounded-xl transition-all duration-300 dynamic-glow"></span>
                  <!-- Effet de survol avec échelle -->
                  <span class="absolute inset-0 bg-gradient-to-r from-[${themeColors['won-pink']}] via-[${themeColors['won-pink']}]/90 to-[${themeColors['won-pink']}]/80 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-105 group-hover:scale-100 shadow-xl"></span>
                  <!-- Contenu du bouton -->
                  <span class="relative flex items-center justify-center z-10 font-bold text-white transition-transform duration-300 group-hover:scale-105">

                <span class="relative z-10 mr-2 flex items-center">
                  Contactez-nous
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              </a>
            </div>
          </div>

          <!-- Particules décoratives intensifiées -->
          <div class="absolute top-0 left-1/4 w-5 h-5 bg-[${themeColors['won-pink']}]/70 rounded-full blur-sm wave-anim"></div>
          <div class="absolute bottom-1/3 right-1/4 w-4 h-4 bg-[${themeColors['won-blue']}]/70 rounded-full blur-sm wave-anim-alt"></div>
          <div class="absolute top-1/3 left-1/2 w-3 h-3 bg-[${themeColors['won-pink']}]/60 rounded-full blur-sm wave-anim" style="animation-delay: 1.2s;"></div>
          <div class="absolute bottom-1/4 right-1/3 w-6 h-6 bg-[${themeColors['won-blue']}]/60 rounded-full blur-md wave-anim-alt" style="animation-delay: 0.8s;"></div>
        </div>
      </div>
    `;
  }
}
